import type { ReactNode } from 'react';
import { Plus } from 'lucide-react';
import {
  ContentKind,
  THREAD_MAX_POSTS,
  threadHelperText,
  type SocialPlatform,
  type ThreadConversionChange,
} from '@mg/shared';
import { Button } from '../../components/ui/Button';
import { useDialogs } from '../../components/ui/DialogProvider';
import type { ThreadPostView } from './thread';
import { ThreadPostCard, type FocusTarget } from './ThreadPostCard';

/** The name "Add post to thread" registers under, for the editor to put focus on. */
export const ADD_POST_FOCUS = ':add-post';

/** Why "Add post to thread" waits while "Show changes" is on. */
export const ADD_POST_CHANGES_SHOWN = 'Turn off Show changes to add a post.';

/**
 * A thread in the editor, in place of the panels of a single post: its posts as an ordered list of
 * cards, then "Add post to thread" with what each post may hold.
 *
 * Every change is the editor's to make (it holds the blocks, and decides where focus goes after
 * it); this component draws the posts and asks before deleting one that has something in it.
 *
 * While an AI conversion's "Show changes" is on (`changesShown`), every post's text is shown
 * read-only, and "Add post to thread" waits: a post added then would be a box nobody could type in.
 * Moving, deleting and adding media change no text, and stay.
 */
export function ThreadEditor({
  posts,
  platform,
  charLimit,
  readOnly,
  busy,
  sending,
  focusTarget,
  onFocusText,
  onTextChange,
  onMove,
  onDelete,
  onDeleteCancelled,
  onAddMedia,
  onAddPost,
  media,
  changesShown,
}: {
  posts: ThreadPostView[];
  platform: SocialPlatform | null;
  charLimit: number | null;
  readOnly: boolean;
  busy: boolean;
  sending: boolean;
  focusTarget: FocusTarget;
  onFocusText: (key: string) => void;
  onTextChange: (postKey: string, key: string, text: string) => void;
  onMove: (postKey: string, delta: -1 | 1) => void;
  onDelete: (postKey: string) => void;
  /** The person kept the post: focus goes back to its Delete button. */
  onDeleteCancelled: (postKey: string) => void;
  onAddMedia: (postKey: string) => void;
  onAddPost: () => void;
  /** The media box of a post, or nothing when it has no media. */
  media: (post: ThreadPostView) => ReactNode;
  /**
   * "Show changes" is on: by the key of each post's text block, the AI's changes to show in it, or
   * null (or no entry) to show its text plain. Absent: every post has its text box.
   */
  changesShown?: ReadonlyMap<string, ThreadConversionChange[] | null>;
}) {
  const { confirm } = useDialogs();
  // Locked posts come first: nothing moves above the first one that is not locked.
  const firstOpen = posts.findIndex((p) => !p.lock);
  const full = posts.length >= THREAD_MAX_POSTS;
  const helper = full
    ? `A thread can have up to ${THREAD_MAX_POSTS} posts.`
    : threadHelperText(platform);

  /** An empty post goes at once; one with text or a file asks first. */
  const remove = async (post: ThreadPostView) => {
    const holdsSomething = post.blocks.some((b) =>
      b.kind === ContentKind.TEXT ? b.text.trim() !== '' : b.assetId != null,
    );
    if (
      holdsSomething &&
      !(await confirm({
        title: 'Delete this post?',
        message: 'Its text and media will be removed from the thread.',
        confirmLabel: 'Delete post',
        danger: true,
      }))
    ) {
      onDeleteCancelled(post.key);
      return;
    }
    onDelete(post.key);
  };

  return (
    <>
      <ol role="list">
        {posts.map((post, i) => {
          const textBlock = post.blocks.find((b) => b.kind === ContentKind.TEXT);
          return (
            <ThreadPostCard
              key={post.key}
              post={post}
              number={i + 1}
              total={posts.length}
              upDisabled={i === 0 || i === firstOpen}
              charLimit={charLimit}
              platform={platform}
              readOnly={readOnly}
              busy={busy}
              sending={sending}
              focusTarget={focusTarget}
              onFocusText={onFocusText}
              onTextChange={(key, text) => onTextChange(post.key, key, text)}
              onMoveUp={() => onMove(post.key, -1)}
              onMoveDown={() => onMove(post.key, 1)}
              onDelete={() => void remove(post)}
              onAddMedia={() => onAddMedia(post.key)}
              media={media(post)}
              review={
                changesShown
                  ? { changes: (textBlock && changesShown.get(textBlock.key)) ?? null }
                  : undefined
              }
            />
          );
        })}
      </ol>
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            ref={focusTarget(ADD_POST_FOCUS)}
            variant="outline"
            size="sm"
            disabled={busy || full || changesShown != null}
            title={changesShown ? ADD_POST_CHANGES_SHOWN : undefined}
            onClick={onAddPost}
          >
            <Plus className="h-4 w-4" aria-hidden />
            Add post to thread
          </Button>
          {helper && <span className="text-xs text-ink-muted">{helper}</span>}
        </div>
      )}
    </>
  );
}
